/**
 * 🌍 Gestão de Timezone - Santo Cartão Admin
 * 
 * Utilitários padronizados para formatação de datas considerando timezone do usuário
 * Baseado no padrão estabelecido em meu-evento/docs/TIMEZONE_MANAGEMENT.md
 */

import moment from 'moment-timezone';
import 'moment/locale/pt-br';

// Configurar moment para português
moment.locale('pt-br');

/**
 * Obtém o timezone do usuário admin logado
 * @returns {string} Timezone do usuário ou fallback para America/Sao_Paulo
 */
export function getUserTimeZone(): string {
  try {
    const usuarioConta = JSON.parse(localStorage.getItem('info_santo_cartao_adm') || '{}');
    return usuarioConta?.timeZone || 'America/Sao_Paulo';
  } catch (error) {
    console.warn('Erro ao obter timezone do usuário:', error);
    return 'America/Sao_Paulo';
  }
}

/**
 * Formata data considerando timezone do usuário
 * @param {string} dateString - Data em formato ISO ou string de data
 * @param {string} format - Formato desejado (padrão: DD/MM/YYYY HH:mm)
 * @param {boolean} isUTC - Se a data está em UTC (padrão: true)
 * @returns {string} Data formatada ou '-' se inválida
 */
export function formatarDataTimezone(
  dateString: string | null | undefined,
  format = 'DD/MM/YYYY HH:mm',
  isUTC = true
): string {
  if (!dateString) return '-';

  try {
    const userTimeZone = getUserTimeZone();
    
    if (isUTC) {
      // ✅ Dados em UTC - aplicar timezone do usuário
      return moment.tz(dateString, userTimeZone).format(format);
    } else {
      // ❌ Dados com timezone local - mostrar como está
      return moment(dateString).format(format);
    }
  } catch (error) {
    console.warn('Erro ao formatar data:', error, dateString);
    return dateString || '-';
  }
}

/**
 * Formata apenas data (sem hora)
 * @param {string} dateString - Data em formato ISO ou string de data
 * @param {boolean} isUTC - Se a data está em UTC (padrão: true)
 * @returns {string} Data formatada (DD/MM/YYYY) ou '-' se inválida
 */
export function formatarDataSomente(
  dateString: string | null | undefined,
  isUTC = true
): string {
  return formatarDataTimezone(dateString, 'DD/MM/YYYY', isUTC);
}

/**
 * Formata data e hora completa
 * @param {string} dateString - Data em formato ISO ou string de data
 * @param {boolean} isUTC - Se a data está em UTC (padrão: true)
 * @returns {string} Data formatada (DD/MM/YYYY HH:mm:ss) ou '-' se inválida
 */
export function formatarDataHoraCompleta(
  dateString: string | null | undefined,
  isUTC = true
): string {
  return formatarDataTimezone(dateString, 'DD/MM/YYYY HH:mm:ss', isUTC);
}

/**
 * Retorna tempo decorrido em formato humanizado
 * @param {string} dateString - Data de criação
 * @returns {string} Tempo decorrido (ex: "há 2 horas")
 */
export function tempoDecorrido(dateString: string | null | undefined): string {
  if (!dateString) return '-';

  try {
    const agora = moment();
    const criacao = moment(dateString);
    return criacao.from(agora);
  } catch (error) {
    console.warn('Erro ao calcular tempo decorrido:', error, dateString);
    return '-';
  }
}

/**
 * Converte data do timezone do usuário para UTC (ao salvar)
 * @param {string} dateInput - Data no formato DD/MM/YYYY
 * @param {string} timeInput - Hora no formato HH:mm
 * @returns {string} Data em formato ISO UTC
 */
export function converterParaUTC(
  dateInput: string,
  timeInput = '00:00'
): string {
  try {
    const userTimeZone = getUserTimeZone();
    const dateTimeString = `${dateInput} ${timeInput}`;
    
    // Parse da data no timezone do usuário
    const userDateTime = moment.tz(dateTimeString, 'DD/MM/YYYY HH:mm', userTimeZone);
    
    // Converter para UTC
    return userDateTime.utc().toISOString();
  } catch (error) {
    console.warn('Erro ao converter data para UTC:', error, dateInput, timeInput);
    return new Date().toISOString();
  }
}

/**
 * Formata data para exibição em formulários (DD/MM/YYYY)
 * @param {string} dateString - Data em formato ISO
 * @param {boolean} isUTC - Se a data está em UTC (padrão: true)
 * @returns {string} Data no formato DD/MM/YYYY para inputs
 */
export function formatarParaInput(
  dateString: string | null | undefined,
  isUTC = true
): string {
  return formatarDataSomente(dateString, isUTC);
}

/**
 * Validações de timezone
 */
export const TIMEZONES_BRASIL = [
  'America/Sao_Paulo',   // Brasília, SP, RJ
  'America/Manaus',      // Manaus, AM
  'America/Fortaleza',   // Fortaleza, CE
  'America/Recife',      // Recife, PE
  'America/Campo_Grande', // Campo Grande, MS
  'America/Cuiaba',      // Cuiabá, MT
  'America/Porto_Velho', // Porto Velho, RO
  'America/Boa_Vista',   // Boa Vista, RR
  'America/Rio_Branco',  // Rio Branco, AC
  'America/Belem',       // Belém, PA
  'America/Maceio',      // Maceió, AL
  'America/Bahia',       // Salvador, BA
  'America/Santarem',    // Santarém, PA
];

/**
 * Verifica se timezone é válido
 * @param {string} timezone - Timezone para validar
 * @returns {boolean} Se o timezone é válido
 */
export function isTimezoneValido(timezone: string): boolean {
  try {
    moment.tz.zone(timezone);
    return true;
  } catch {
    return false;
  }
}

// Backward compatibility - funções antigas
export { formatarDataTimezone as formatarData };
export { getUserTimeZone as getUsuarioTimeZone };